import React, { useMemo } from 'react';
import {
  format,
  parseISO,
  setHours,
  getHours,
  getMinutes,
  isSameDay,
  isToday
} from 'date-fns';
import EventPill from './EventPill';

/**
 * DayView - Single day timeline view
 *
 * Features:
 * - Full day timeline
 * - Hourly rows with half-hour lines
 * - Events positioned by time
 * - Click on time slot to quick-add
 * - Current time indicator
 * - All-day events at top
 */

const HOURS_START = 0; // Midnight
const HOURS_END = 24; // End of day
const SLOT_HEIGHT = 60; // pixels per hour (taller for day view)

const DayView = ({
  currentDate,
  events = [],
  onDateClick,
  onEventClick,
  loading
}) => {
  // Generate hours
  const hours = useMemo(() => {
    const result = [];
    for (let h = HOURS_START; h < HOURS_END; h++) {
      result.push(h);
    }
    return result;
  }, []);

  // Filter events for current day
  const dayEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.startTime) return false;
      try {
        const eventDate = parseISO(event.startTime);
        return isSameDay(eventDate, currentDate);
      } catch {
        return false;
      }
    }).sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      return new Date(a.startTime) - new Date(b.startTime);
    });
  }, [events, currentDate]);

  // Split into all-day and timed events
  const allDayEvents = dayEvents.filter(e => e.allDay);
  const timedEvents = dayEvents.filter(e => !e.allDay);

  /**
   * Calculate overlapping event positions
   *
   * Layout Rules:
   * 1. Same start AND end time → Side-by-side (equal width columns)
   * 2. Different durations or one starts within another → Layered with offset
   * 3. Content: base layer = top aligned, overlapping layers = center aligned
   * 4. If title would be hidden behind layered event → use side-by-side instead
   */
  const eventLayout = useMemo(() => {
    if (timedEvents.length === 0) return {};

    // Parse event times
    const eventTimes = timedEvents.map(event => {
      const start = parseISO(event.startTime);
      const end = event.endTime ? parseISO(event.endTime) : start;
      const startMinutes = getHours(start) * 60 + getMinutes(start);
      const endMinutes = getHours(end) * 60 + getMinutes(end);
      const duration = Math.max(endMinutes - startMinutes, 30);
      return {
        id: event.id,
        start: startMinutes,
        end: Math.max(endMinutes, startMinutes + 30),
        duration
      };
    });

    // Group events that have IDENTICAL start AND end times (side-by-side)
    const identicalTimeGroups = {};
    eventTimes.forEach(event => {
      const key = `${event.start}-${event.end}`;
      if (!identicalTimeGroups[key]) {
        identicalTimeGroups[key] = [];
      }
      identicalTimeGroups[key].push(event);
    });

    // Sort by start time, then by duration (longer first for base layer)
    eventTimes.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return (b.duration) - (a.duration); // Longer duration first
    });

    const layout = {}; // eventId -> { column, totalColumns, layer, offsetPercent, widthPercent, zIndex, contentAlign }

    eventTimes.forEach((event) => {
      const timeKey = `${event.start}-${event.end}`;
      const identicalGroup = identicalTimeGroups[timeKey];

      // Check if this event has identical-time siblings (same start AND end)
      if (identicalGroup.length > 1) {
        // Side-by-side layout for identical times
        const columnIndex = identicalGroup.findIndex(e => e.id === event.id);
        const totalColumns = identicalGroup.length;
        const widthPercent = 100 / totalColumns;
        const offsetPercent = columnIndex * widthPercent;

        layout[event.id] = {
          mode: 'side-by-side',
          column: columnIndex,
          totalColumns,
          offsetPercent,
          widthPercent,
          zIndex: 1,
          contentAlign: 'top' // All side-by-side events align top
        };
        return;
      }

      // Find all events that overlap with this one (but NOT identical times)
      const overlapping = eventTimes.filter(other =>
        other.id !== event.id &&
        other.start < event.end &&
        other.end > event.start &&
        !(other.start === event.start && other.end === event.end) // Exclude identical times
      );

      if (overlapping.length === 0) {
        // No overlaps - full width
        layout[event.id] = {
          mode: 'single',
          layer: 0,
          offsetPercent: 0,
          widthPercent: 100,
          zIndex: 1,
          contentAlign: 'top'
        };
      } else {
        // Find events that have already been positioned and overlap with this one
        const positionedOverlaps = overlapping.filter(o => layout[o.id] && layout[o.id].mode !== 'side-by-side');

        if (positionedOverlaps.length === 0) {
          // First event in overlap group - takes full width, content at top
          layout[event.id] = {
            mode: 'layered',
            layer: 0,
            offsetPercent: 0,
            widthPercent: 100,
            zIndex: 1,
            contentAlign: 'top' // Base layer: content at top
          };
        } else {
          // Calculate layer based on positioned overlaps
          const maxLayer = Math.max(...positionedOverlaps.map(o => layout[o.id].layer));
          const newLayer = maxLayer + 1;

          // Offset increases with each layer (15% per layer for better visibility)
          const offsetPercent = Math.min(newLayer * 15, 40); // Cap at 40%

          // Width decreases with each layer
          const widthPercent = 100 - offsetPercent;

          layout[event.id] = {
            mode: 'layered',
            layer: newLayer,
            offsetPercent,
            widthPercent,
            zIndex: newLayer + 1, // Higher layer = higher z-index (on top)
            contentAlign: 'center' // Overlapping layers: content centered
          };
        }
      }
    });

    return layout;
  }, [timedEvents]);

  // Calculate event position and height
  const getEventStyle = (event) => {
    if (event.allDay) return null;

    try {
      const start = parseISO(event.startTime);
      const end = event.endTime ? parseISO(event.endTime) : start;

      const startHour = getHours(start) + getMinutes(start) / 60;
      const endHour = getHours(end) + getMinutes(end) / 60;

      const top = (startHour - HOURS_START) * SLOT_HEIGHT;
      const height = Math.max((endHour - startHour) * SLOT_HEIGHT, SLOT_HEIGHT / 2);

      return {
        top: `${top}px`,
        height: `${height}px`,
        minHeight: '30px'
      };
    } catch {
      return null;
    }
  };

  // Handle time slot click
  const handleTimeSlotClick = (hour, e) => {
    if (onDateClick) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const rect = e.currentTarget.getBoundingClientRect();
      onDateClick(currentDate, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      }, time);
    }
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  const isCurrentDay = isToday(currentDate);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="select-none">
      {/* Day header - compact inline format */}
      <div className={`px-4 py-2 border-b border-gray-200 ${isCurrentDay ? 'bg-yellow-50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center gap-2">
          <span className={`text-sm font-medium ${isCurrentDay ? 'text-yellow-600' : 'text-gray-700'}`}>
            {format(currentDate, 'EEEE')}
          </span>
          <span className="text-gray-300">·</span>
          <span className={`text-sm ${isCurrentDay ? 'text-yellow-600' : 'text-gray-600'}`}>
            {format(currentDate, 'MMMM d, yyyy')}
          </span>
        </div>
      </div>

      {/* All-day events */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-gray-200 bg-white p-3">
          <div className="text-xs font-medium text-gray-500 mb-2">All Day</div>
          <div className="space-y-1">
            {allDayEvents.map(event => (
              <EventPill
                key={event.id}
                event={event}
                onClick={handleEventClick}
                size="md"
                showTime={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Time grid */}
      <div className="flex overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
        {/* Time labels column */}
        <div
          className="w-14 sm:w-20 flex-shrink-0 border-r border-gray-200 bg-white"
          style={{ minHeight: `${(HOURS_END - HOURS_START) * SLOT_HEIGHT}px` }}
        >
          {hours.map((hour, index) => (
            <div
              key={hour}
              className="relative"
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              <span className={`absolute left-0 right-0 text-center text-xs text-gray-500 ${
                index === 0 ? 'top-1' : 'top-0 -translate-y-1/2'
              }`}>
                <span className="hidden sm:inline">{format(setHours(new Date(), hour), 'h a')}</span>
                <span className="sm:hidden">{format(setHours(new Date(), hour), 'ha').toLowerCase()}</span>
              </span>
            </div>
          ))}
        </div>

        {/* Events column */}
        <div className={`flex-1 relative ${isCurrentDay ? 'bg-yellow-50/30' : 'bg-white'}`}>
          {/* Hour slots */}
          {hours.map(hour => (
            <div
              key={hour}
              onClick={(e) => handleTimeSlotClick(hour, e)}
              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
              style={{ height: `${SLOT_HEIGHT}px` }}
            >
              {/* Half-hour line */}
              <div className="absolute left-0 right-0 border-t border-gray-100 border-dashed"
                   style={{ top: `${(hour - HOURS_START) * SLOT_HEIGHT + SLOT_HEIGHT / 2}px` }} />
            </div>
          ))}

          {/* Events overlay */}
          <div className="absolute inset-0 pointer-events-none px-1">
            {timedEvents.map(event => {
              const style = getEventStyle(event);
              if (!style) return null;

              // Get layout positioning (side-by-side or layered)
              const layoutInfo = eventLayout[event.id] || {
                offsetPercent: 0,
                widthPercent: 100,
                zIndex: 1,
                contentAlign: 'top'
              };

              return (
                <div
                  key={event.id}
                  className="absolute pointer-events-auto px-0.5"
                  style={{
                    ...style,
                    left: `${layoutInfo.offsetPercent}%`,
                    width: `${layoutInfo.widthPercent}%`,
                    zIndex: layoutInfo.zIndex
                  }}
                >
                  <EventPillExpanded
                    event={event}
                    onClick={handleEventClick}
                    contentAlign={layoutInfo.contentAlign || 'top'}
                  />
                </div>
              );
            })}
          </div>

          {/* Current time indicator */}
          {isCurrentDay && (
            <CurrentTimeIndicator hoursStart={HOURS_START} slotHeight={SLOT_HEIGHT} />
          )}
        </div>
      </div>

      {/* Summary footer */}
      <div className="p-3 bg-gray-50 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          {dayEvents.length === 0 ? (
            'No events scheduled'
          ) : (
            <>
              {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''} today
              {allDayEvents.length > 0 && ` (${allDayEvents.length} all-day)`}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Expanded event pill for day view (shows more detail)
 * @param {string} contentAlign - 'top' | 'center' - Vertical alignment of content
 */
const EventPillExpanded = ({ event, onClick, contentAlign = 'top' }) => {
  // Category colors (must match EventPill.js CATEGORY_COLORS)
  const categoryColors = {
    external: {
      bg: 'bg-blue-50',
      border: 'border-l-blue-500',
      text: 'text-blue-900',
      time: 'text-blue-600'
    },
    internal: {
      bg: 'bg-purple-50',
      border: 'border-l-purple-500',
      text: 'text-purple-900',
      time: 'text-purple-600'
    },
    personal: {
      bg: 'bg-green-50',
      border: 'border-l-green-500',
      text: 'text-green-900',
      time: 'text-green-600'
    },
    schedule: {
      bg: 'bg-orange-50',
      border: 'border-l-orange-500',
      text: 'text-orange-900',
      time: 'text-orange-600'
    }
  };

  const colors = categoryColors[event.category] || categoryColors.external;

  // Content alignment classes
  const alignmentClasses = {
    top: 'justify-start',
    center: 'justify-center'
  };

  // Format time
  const getTimeRange = () => {
    try {
      const start = parseISO(event.startTime);
      const end = event.endTime ? parseISO(event.endTime) : null;

      if (end) {
        return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
      }
      return format(start, 'h:mm a');
    } catch {
      return '';
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick(event);
  };

  return (
    <button
      onClick={handleClick}
      className={`
        w-full h-full text-left rounded-lg p-2 flex flex-col
        border-l-4 ${colors.border} ${colors.bg}
        hover:shadow-md transition-shadow cursor-pointer
        overflow-hidden ${alignmentClasses[contentAlign] || 'justify-start'}
      `}
    >
      <div className={`font-medium ${colors.text} truncate`}>
        {event.title}
      </div>
      <div className={`text-xs ${colors.time} mt-0.5`}>
        {getTimeRange()}
      </div>
    </button>
  );
};

/**
 * Current time indicator line
 */
const CurrentTimeIndicator = ({ hoursStart, slotHeight }) => {
  const now = new Date();
  const currentHour = getHours(now) + getMinutes(now) / 60;
  const top = (currentHour - hoursStart) * slotHeight;

  return (
    <div
      className="absolute left-0 right-0 flex items-center pointer-events-none z-10"
      style={{ top: `${top}px` }}
    >
      <div className="w-3 h-3 rounded-full bg-red-500 -ml-1.5"></div>
      <div className="flex-1 h-0.5 bg-red-500"></div>
    </div>
  );
};

export default DayView;
